import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SubUserForm from '../components/SubUserForm';
import SubUserLogs from '../components/SubUserLogs';
import instance from '../utils/axiosConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ marginTop: '20px' }}>
      {value === index && children}
    </div>
  );
}

const SubUsersPage = () => {
  const [subUsers, setSubUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchSubUsers();
  }, []);

  const fetchSubUsers = async () => {
    try {
      const response = await instance.get('/api/sub-users');
      setSubUsers(response.data);
    } catch (error) {
      showSnackbar('Error al cargar sub-usuarios', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este sub-usuario?')) {
      try {
        await instance.delete(`/api/sub-users/${id}`);
        showSnackbar('Sub-usuario eliminado exitosamente', 'success');
        fetchSubUsers();
      } catch (error) {
        showSnackbar('Error al eliminar sub-usuario', 'error');
      }
    }
  };

  const handleSubmit = async (userData) => {
    try {
      if (editingUser) {
        await instance.put(`/api/sub-users/${editingUser._id}`, userData);
        showSnackbar('Sub-usuario actualizado exitosamente', 'success');
      } else {
        await instance.post('/api/sub-users', userData);
        showSnackbar('Sub-usuario creado exitosamente', 'success');
      }
      setOpenDialog(false);
      setEditingUser(null);
      fetchSubUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al procesar sub-usuario', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Sub-usuarios" />
          <Tab label="Logs de Actividad" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5">Sub-usuarios</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingUser(null);
              setOpenDialog(true);
            }}
          >
            Nuevo Sub-usuario
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Permisos</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.status}</TableCell>
                  <TableCell>
                    {Object.entries(user.permissions)
                      .filter(([key, value]) => value)
                      .map(([key]) => key)
                      .join(', ')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setEditingUser(user);
                        setOpenDialog(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(user._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <SubUserLogs />
      </TabPanel>

      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setEditingUser(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <SubUserForm
          onSubmit={handleSubmit}
          onCancel={() => {
            setOpenDialog(false);
            setEditingUser(null);
          }}
          initialData={editingUser}
        />
      </Dialog>
    </Box>
  );
};

export default SubUsersPage; 